import React, { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { Trophy, Users, BadgeCheck, TrendingUp, Lock, Search, Bookmark, ArrowBigUp } from "lucide-react";
import { Panel, Skeleton, EmptyState, GainBadge } from "../components/ui";
import Disclaimer from "../components/Disclaimer";
import { api } from "../lib/api";
import { useAuth } from "../context/AuthContext";
import VerifiedBadge from "../components/VerifiedBadge";
import { useTranslation } from "react-i18next";
import useSeo from "../lib/useSeo";
import TierBadge from "../components/TierBadge";
import Avatar from "../components/Avatar";
import { Segmented } from "../components/ui";
import { toast } from "sonner";

const medal = (rank) =>
  rank === 1 ? "bg-amber-100 text-amber-700"
  : rank === 2 ? "bg-slate-200 text-slate-600"
  : rank === 3 ? "bg-orange-100 text-orange-700"
  : "bg-slate-100 text-slate-400";

export default function Investors() {
  const { t } = useTranslation();
  const { isPaid } = useAuth();
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const [q, setQ] = useState("");
  const [sort, setSort] = useState("return");

  useSeo({
    title: t("seo.investorsTitle"),
    description: t("seo.investorsDesc"),
    jsonLd: items.length ? {
      "@context": "https://schema.org",
      "@type": "ItemList",
      itemListElement: items.slice(0, 20).map((inv, i) => ({
        "@type": "ListItem", position: i + 1, name: inv.display_name,
        url: `${window.location.origin}/investors/${inv.investor_id}`,
      })),
    } : null,
  });

  // Debounced so typing doesn't fire a request per keystroke.
  useEffect(() => {
    const timer = setTimeout(() => {
      api.searchInvestors({ q: q.trim() || undefined, sort })
        .then((r) => setItems(r.data)).catch(() => setItems([])).finally(() => setLoading(false));
    }, q ? 350 : 0);
    return () => clearTimeout(timer);
  }, [q, sort]);

  const toggleBookmark = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const { data } = await api.bookmarkInvestor(id);
      setItems((xs) => xs.map((x) => x.investor_id === id ? { ...x, is_bookmarked: data.bookmarked } : x));
    } catch { toast.error(t("dialogs.failed")); }
  };

  const toggleUpvote = async (e, id) => {
    e.preventDefault(); e.stopPropagation();
    try {
      const { data } = await api.upvoteInvestor(id);
      setItems((xs) => xs.map((x) => x.investor_id === id
        ? { ...x, is_upvoted: data.upvoted, upvotes: data.upvotes } : x));
    } catch (err) { toast.error(err.response?.data?.detail || t("dialogs.failed")); }
  };

  return (
    <div className="space-y-6">
      <div>
        <span className="label">{t("investors.subtitle")}</span>
        <h1 className="text-3xl sm:text-4xl font-heading font-semibold tracking-tighter mt-1">
          {t("investors.title")}
        </h1>
      </div>

      {!isPaid && (
        <Panel className="card p-5 bg-brand-50/50 border-brand-100" testid="investors-upsell">
          <div className="flex flex-col sm:flex-row sm:items-center gap-4 justify-between">
            <div className="flex gap-3">
              <Lock size={20} className="text-brand-600 shrink-0 mt-0.5" />
              <div>
                <div className="font-medium text-slate-900">{t("investors.upsellTitle")}</div>
                <p className="text-sm text-slate-600 mt-1">{t("investors.upsellBody")}</p>
              </div>
            </div>
            <Link to="/billing" className="btn-primary shrink-0 justify-center" data-testid="investors-upgrade-btn">
              {t("billing.upgrade")}
            </Link>
          </div>
        </Panel>
      )}

      <div className="flex flex-col sm:flex-row gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 text-slate-400" size={16} />
          <input className="input pl-10 py-2.5 text-sm" data-testid="investor-search"
                 placeholder={t("investors.searchPlaceholder")} value={q}
                 onChange={(e) => setQ(e.target.value)} />
        </div>
        <Segmented
          options={[
            { value: "return", label: t("investors.sortReturn") },
            { value: "followers", label: t("investors.sortFollowers") },
            { value: "upvotes", label: t("investors.sortUpvotes") },
          ]}
          value={sort} onChange={setSort} testid="investor-sort"
        />
      </div>

      {loading ? (
        <div className="space-y-3">{[0, 1, 2].map((i) => <Skeleton key={i} className="h-24" />)}</div>
      ) : items.length === 0 ? (
        <EmptyState testid="investors-empty" title={t("investors.emptyTitle")} description={t("investors.emptyDesc")} />
      ) : (
        <div className="space-y-3" data-testid="investors-list">
          {items.map((inv) => (
            <Link
              key={inv.investor_id}
              to={`/investors/${inv.investor_id}`}
              data-testid={`investor-card-${inv.investor_id}`}
              className="card p-4 sm:p-5 flex items-center gap-4 hover:shadow-soft transition-shadow"
            >
              <div className={`w-9 h-9 rounded-xl grid place-items-center font-heading font-semibold text-sm shrink-0 ${medal(inv.rank)}`}>
                {inv.rank}
              </div>
              <Avatar src={inv.avatar} name={inv.display_name} size={38} />
              <div className="min-w-0 flex-1">
                <div className="flex items-center gap-1.5">
                  <span className="font-medium text-slate-900 truncate">{inv.display_name}</span>
                  {inv.verified && <VerifiedBadge size={15} />}
                  <TierBadge tier={inv.tier} size="xs" />
                </div>
                {inv.username && <div className="text-xs text-slate-400 truncate">@{inv.username}</div>}
                {inv.headline && <div className="text-xs text-slate-400 truncate">{inv.headline}</div>}
                <div className="flex items-center gap-3 mt-1 text-xs text-slate-400">
                  <span className="flex items-center gap-1"><Users size={12} />{inv.follower_count}</span>
                  <span className="flex items-center gap-1"><TrendingUp size={12} />{inv.position_count}</span>
                </div>
              </div>
              <div className="flex items-center gap-1 shrink-0">
                <button onClick={(e) => toggleUpvote(e, inv.investor_id)}
                        data-testid={`upvote-${inv.investor_id}`}
                        title={t("investors.upvote")}
                        className={`flex flex-col items-center rounded-lg px-2 py-1 transition-colors ${
                          inv.is_upvoted ? "text-brand-600 bg-brand-50" : "text-slate-300 hover:text-brand-600"}`}>
                  <ArrowBigUp size={17} />
                  {inv.upvotes > 0 && <span className="text-[10px] tabular font-semibold">{inv.upvotes}</span>}
                </button>
                <button onClick={(e) => toggleBookmark(e, inv.investor_id)}
                        data-testid={`bookmark-${inv.investor_id}`}
                        title={t("investors.bookmark")}
                        className={`p-2 rounded-lg transition-colors ${
                          inv.is_bookmarked ? "text-brand-600" : "text-slate-300 hover:text-brand-600"}`}>
                  <Bookmark size={16} fill={inv.is_bookmarked ? "currentColor" : "none"} />
                </button>
              </div>

              <div className="text-right shrink-0">
                {inv.return_pct === null || inv.return_pct === undefined ? (
                  <span className="text-xs text-slate-400">{t("investors.notRanked")}</span>
                ) : (
                  <>
                    <GainBadge value={inv.return_pct} />
                    <div className="text-[11px] text-slate-400 mt-1">{t("investors.perYear")}</div>
                  </>
                )}
              </div>
            </Link>
          ))}
        </div>
      )}

      <Disclaimer />
    </div>
  );
}
